import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2, Play } from 'lucide-react';
import {
  addQuickstartCompetencyBundle,
  removeQuickstartCompetencyBundle,
  connectQuickstartDisplay,
  createTeachingUnitApi,
  disconnectQuickstartDisplay,
  fetchTeachingMeta,
  fetchTeachingQuickstartContext,
  fetchTeachingQuickstartStudentCompetencySummary,
  updateTeachingUnitApi,
  type QuickstartSitzplanSuggestion
} from '../lib/unterrichtenApi';
import type {
  LessonPhase,
  LiveLessonCompetency,
  LiveLessonDisplayOverlays,
  LiveSessionListItem,
  StudentObservation,
  TemporaryNeedHelpState
} from '../lib/liveLessonTypes';
import {
  buildInitialLiveSession,
  fetchLiveLessonCompetencies,
  fetchLiveLessonSession,
  fetchLiveSessions,
  fetchTimetableNowWidget,
  destroyLiveSession,
  reactivateLiveSession,
  saveLiveLessonSession,
  suggestLiveLessonCompetencies
} from '../lib/liveLessonApi';
import LiveSessionHistoryList from '../components/liveLesson/LiveSessionHistoryList';
import { useAcousticRoomAnalyser } from '../hooks/useAcousticRoomAnalyser';
import { Button, Tile, TileContent, TileHeader, TileTitle } from '../components/ui';
import CompetencyQuickPicker from '../components/liveLesson/CompetencyQuickPicker';
import LessonCompetencyPanel from '../components/liveLesson/LessonCompetencyPanel';
import LessonPhaseTimeline from '../components/liveLesson/LessonPhaseTimeline';
import LiveSessionReview from '../components/liveLesson/LiveSessionReview';
import RoomEnergyWidget from '../components/liveLesson/RoomEnergyWidget';
import SeatingLiveCanvas, { type LiveStudentSeat } from '../components/liveLesson/SeatingLiveCanvas';
import StudentSeatContextMenu, { type StudentSeatMenuItem } from '../components/liveLesson/StudentSeatContextMenu';
import StudentSeatGroupActionsMenu from '../components/liveLesson/StudentSeatGroupActionsMenu';
import EmailModal from '../components/EmailModal';
import { applySeatSelectionClick } from '../lib/liveLessonSeatSelection';
import {
  endTemporaryAbsence,
  fetchActiveAbsences,
  startTemporaryAbsence,
  type ActiveAbsenceMap
} from '../lib/temporaryAbsencesApi';
import LessonSessionManagement from '../components/liveLesson/LessonSessionManagement';
import TeacherControlDock from '../components/liveLesson/TeacherControlDock';
import { LiveSessionProvider, useLiveSession } from '../components/liveLesson/LiveSessionProvider';

const PHASES: LessonPhase[] = [
  { id: 'einstieg', label: 'Einstieg', order: 1, defaultDurationSec: 5 * 60, acousticProfile: 'normal' },
  { id: 'erarbeitung', label: 'Erarbeitung', order: 2, defaultDurationSec: 15 * 60, acousticProfile: 'lively' },
  { id: 'sicherung', label: 'Sicherung', order: 3, defaultDurationSec: 10 * 60, acousticProfile: 'quiet' },
  { id: 'reflexion', label: 'Reflexion', order: 4, defaultDurationSec: 8 * 60, acousticProfile: 'quiet' }
];

type QuickstartStudent = {
  id: string;
  firstName: string;
  lastName: string;
};

type LiveLessonShellProps = {
  teachingUnitId: number;
  className: string;
  theme: string;
  subjectId?: number;
  onExitToOverview: () => void;
};

function LiveLessonShell({ teachingUnitId, className, theme, subjectId, onExitToOverview }: LiveLessonShellProps) {
  const { session, observations, helpStates, loading, setSession, setObservations, setHelpStates, saveAll, emitEvent, syncBoardState, commitReview, discardReview } =
    useLiveSession();
  const [students, setStudents] = useState<QuickstartStudent[]>([]);
  const [bookingCounts, setBookingCounts] = useState<Record<string, number>>({});
  const [curriculumCompetencies, setCurriculumCompetencies] = useState<LiveLessonCompetency[]>([]);
  const [competencyLoading, setCompetencyLoading] = useState(false);
  const [competencySuggesting, setCompetencySuggesting] = useState(false);
  const [competencySuggestSource, setCompetencySuggestSource] = useState<'llm' | 'keywords' | 'none' | null>(null);
  const [sitzplanSuggestions, setSitzplanSuggestions] = useState<QuickstartSitzplanSuggestion[]>([]);
  const [selectedSitzplanId, setSelectedSitzplanId] = useState<number | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<LiveStudentSeat | null>(null);
  const [seatMenu, setSeatMenu] = useState<{ student: LiveStudentSeat; x: number; y: number } | null>(null);
  const [activeAbsences, setActiveAbsences] = useState<ActiveAbsenceMap>({});
  const [seatActionStudentId, setSeatActionStudentId] = useState<string | null>(null);
  const [showCompetencyPicker, setShowCompetencyPicker] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailStudents, setEmailStudents] = useState<LiveStudentSeat[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());
  const selectionAnchorRef = useRef<number | null>(null);
  const [displayPinInput, setDisplayPinInput] = useState('');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [timerSec, setTimerSec] = useState(10 * 60);
  const [timerRunning, setTimerRunning] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [contextLoading, setContextLoading] = useState(true);
  const [endingSession, setEndingSession] = useState(false);
  const [destroyingSession, setDestroyingSession] = useState(false);
  const acoustic = useAcousticRoomAnalyser({ enabled: audioEnabled });
  const sessionHydratedRef = useRef(false);
  const competencySuggestRanRef = useRef(false);
  const persistMetaRef = useRef('');

  useEffect(() => {
    if (!session || loading) return;
    if (sessionHydratedRef.current) return;
    sessionHydratedRef.current = true;
    if (session.timerRemainingSec != null) setTimerSec(session.timerRemainingSec);
    if (session.timerRunning != null) setTimerRunning(session.timerRunning);
    if (session.sitzplanId != null) setSelectedSitzplanId(session.sitzplanId);
    if (session.displayPin) setDisplayPinInput(session.displayPin);
    persistMetaRef.current = JSON.stringify({
      theme,
      subjectId: subjectId ?? null,
      sitzplanId: session.sitzplanId ?? selectedSitzplanId,
      timerSec: session.timerRemainingSec ?? timerSec,
      timerRunning: session.timerRunning ?? timerRunning,
      className
    });
  }, [session, loading]);

  useEffect(() => {
    sessionHydratedRef.current = false;
    competencySuggestRanRef.current = false;
  }, [teachingUnitId]);

  useEffect(() => {
    if (!session || loading || session.status === 'ended') return;
    const snapshot = JSON.stringify({
      theme,
      subjectId: subjectId ?? null,
      sitzplanId: selectedSitzplanId,
      timerSec,
      timerRunning,
      className
    });
    if (snapshot === persistMetaRef.current) return;

    const handle = window.setTimeout(() => {
      persistMetaRef.current = snapshot;
      const next = {
        ...session,
        theme,
        subjectId: subjectId ?? null,
        sitzplanId: selectedSitzplanId,
        timerRemainingSec: timerSec,
        timerRunning,
        classContext: { parentType: 'class' as const, label: className.trim() || session.classContext.label }
      };
      void saveAll(next, observations, helpStates).catch(() => {});
    }, 1500);
    return () => window.clearTimeout(handle);
  }, [
    session,
    loading,
    theme,
    subjectId,
    selectedSitzplanId,
    timerSec,
    timerRunning,
    className,
    observations,
    helpStates,
    saveAll
  ]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setContextLoading(true);
      try {
        const [context, summary] = await Promise.all([
          fetchTeachingQuickstartContext({ className, subjectId, theme, light: true }),
          fetchTeachingQuickstartStudentCompetencySummary(teachingUnitId)
        ]);
        if (!mounted) return;
        setStudents(
          (context.students || []).map((entry) => ({
            id: entry.sophomorix_unid || entry.id,
            firstName: entry.first_name,
            lastName: entry.last_name
          }))
        );
        setSitzplanSuggestions(context.sitzplanSuggestions || []);
        setSelectedSitzplanId(context.suggestedSitzplanId || context.sitzplanSuggestions?.[0]?.id || null);
        const counts: Record<string, number> = {};
        (summary || []).forEach((entry) => {
          counts[entry.studentId] = entry.quickstartBundleCount || 0;
        });
        setBookingCounts(counts);
      } catch (error: any) {
        toast.error(error?.response?.data?.error || 'Live-Kontext konnte nicht geladen werden');
      } finally {
        if (mounted) setContextLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [className, subjectId, teachingUnitId, theme]);

  useEffect(() => {
    let mounted = true;
    void fetchActiveAbsences('teaching_unit', teachingUnitId)
      .then((result) => {
        if (mounted) setActiveAbsences(result.data || {});
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [teachingUnitId]);

  useEffect(() => {
    if (!session || loading) return;
    let mounted = true;
    const loadCompetencies = async () => {
      setCompetencyLoading(true);
      try {
        const envelope = await fetchLiveLessonCompetencies(teachingUnitId, { subjectId, theme });
        if (!mounted) return;
        setCurriculumCompetencies(envelope.competencies);

        const activeIds = session.activeCompetencyIds?.length
          ? session.activeCompetencyIds
          : envelope.activeCompetencyIds;
        const suggestedIds = session.suggestedCompetencyIds?.length
          ? session.suggestedCompetencyIds
          : envelope.suggestedCompetencyIds;
        const source =
          session.competencySuggestSource ??
          (envelope.suggestSource as 'llm' | 'keywords' | 'none' | null) ??
          null;
        setCompetencySuggestSource(source);

        if (activeIds.length > 0 || suggestedIds.length > 0) {
          const needsUpdate =
            JSON.stringify(activeIds) !== JSON.stringify(session.activeCompetencyIds || []) ||
            JSON.stringify(suggestedIds) !== JSON.stringify(session.suggestedCompetencyIds || []);
          if (needsUpdate) {
            setSession({
              ...session,
              activeCompetencyIds: activeIds,
              suggestedCompetencyIds: suggestedIds,
              competencySuggestSource: source
            });
          }
          return;
        }

        if (theme.trim().length >= 3 && subjectId && !competencySuggestRanRef.current) {
          competencySuggestRanRef.current = true;
          setCompetencySuggesting(true);
          const suggested = await suggestLiveLessonCompetencies(teachingUnitId, { subjectId, theme });
          if (!mounted) return;
          setCurriculumCompetencies(suggested.competencies);
          setCompetencySuggestSource(suggested.suggestSource ?? null);
          setSession(
            suggested.session ?? {
              ...session,
              activeCompetencyIds: suggested.activeCompetencyIds,
              suggestedCompetencyIds: suggested.suggestedCompetencyIds,
              competencySuggestSource: suggested.suggestSource ?? null
            }
          );
        }
      } catch (error: unknown) {
        const err = error as { response?: { data?: { error?: string } } };
        toast.error(err.response?.data?.error || 'Kompetenzen konnten nicht geladen werden');
      } finally {
        if (mounted) {
          setCompetencyLoading(false);
          setCompetencySuggesting(false);
        }
      }
    };
    void loadCompetencies();
    return () => {
      mounted = false;
    };
  }, [
    loading,
    teachingUnitId,
    subjectId,
    theme,
    session,
    setSession
  ]);

  useEffect(() => {
    if (!timerRunning) return;
    const handle = window.setInterval(() => {
      setTimerSec((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(handle);
  }, [timerRunning]);

  const liveLessonDisplayOverlays = useMemo<LiveLessonDisplayOverlays>(() => {
    const studentById = new Map(students.map((student) => [student.id, student]));
    const toOverlay = (studentId: string) => {
      const student = studentById.get(studentId);
      return {
        studentId,
        firstName: student?.firstName || '',
        lastName: student?.lastName || ''
      };
    };
    return {
      absences: Object.keys(activeAbsences).map(toOverlay),
      help: helpStates.filter((entry) => entry.active).map((entry) => toOverlay(entry.studentId))
    };
  }, [activeAbsences, helpStates, students]);

  useEffect(() => {
    if (!session) return;
    const phaseLabel = PHASES.find((entry) => entry.id === session.currentPhaseId)?.label || null;
    const roomBand = acoustic.signal
      ? acoustic.signal.phaseFit === 'high'
        ? 'lively'
        : acoustic.signal.phaseFit === 'ok'
        ? 'balanced'
        : 'quiet'
      : 'quiet';

    if (!session.displayPin) return;
    void syncBoardState(
      session.displayPin,
      {
        enabled: true,
        scene: session.activeScene,
        phaseLabel,
        timer: { remainingSec: timerSec, running: timerRunning },
        workMode: session.workMode,
        taskText: theme,
        progress: {
          completedPhaseIds: PHASES.filter((entry) => entry.order < (PHASES.find((p) => p.id === session.currentPhaseId)?.order || 0)).map((entry) => entry.id),
          total: PHASES.length
        },
        roomEnergy: {
          band: roomBand,
          trend: acoustic.signal?.trend
        },
        updatedAt: new Date().toISOString(),
        version: (session.boardStateVersion || 0) + 1
      },
      liveLessonDisplayOverlays
    ).catch(() => {});
  }, [session, acoustic.signal, syncBoardState, theme, timerRunning, timerSec, liveLessonDisplayOverlays]);

  const mappedStudents = useMemo<LiveStudentSeat[]>(
    () =>
      students.map((student) => ({
        ...student,
        bookingCount: bookingCounts[student.id] || 0
      })),
    [students, bookingCounts]
  );

  const selectedSitzplan = useMemo(
    () => sitzplanSuggestions.find((plan) => plan.id === selectedSitzplanId) || null,
    [sitzplanSuggestions, selectedSitzplanId]
  );

  const seatLayout = useMemo(() => {
    if (!selectedSitzplan) {
      return {
        columns: 8,
        slots: mappedStudents as Array<LiveStudentSeat | null>
      };
    }

    const occupied = selectedSitzplan.positionen.filter((entry) => !!entry?.sophomorix_unid);
    const rows = occupied.length
      ? Array.from(new Set(occupied.map((entry) => Number(entry.grid_row)))).sort((a, b) => a - b)
      : Array.from({ length: Math.max(1, selectedSitzplan.grid_rows || 1) }, (_, idx) => idx);
    const cols = occupied.length
      ? Array.from(new Set(occupied.map((entry) => Number(entry.grid_col)))).sort((a, b) => a - b)
      : Array.from({ length: Math.max(1, selectedSitzplan.grid_cols || 1) }, (_, idx) => idx);

    const byStudentId = new Map(mappedStudents.map((entry) => [entry.id, entry]));
    const slots: Array<LiveStudentSeat | null> = [];
    rows.forEach((row) => {
      cols.forEach((col) => {
        const pos = selectedSitzplan.positionen.find((entry) => Number(entry.grid_row) === row && Number(entry.grid_col) === col);
        const studentId = pos?.sophomorix_unid ? String(pos.sophomorix_unid) : '';
        slots.push(studentId ? byStudentId.get(studentId) || null : null);
      });
    });

    return {
      columns: Math.max(1, cols.length),
      slots
    };
  }, [mappedStudents, selectedSitzplan]);

  useEffect(() => {
    setSelectedStudentIds(new Set());
    selectionAnchorRef.current = null;
  }, [selectedSitzplanId, teachingUnitId]);

  const selectedStudents = useMemo(
    () => mappedStudents.filter((student) => selectedStudentIds.has(student.id)),
    [mappedStudents, selectedStudentIds]
  );

  const handleStudentSelect = (
    student: LiveStudentSeat,
    index: number,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    const { nextSelection, anchorIndex } = applySeatSelectionClick({
      slots: seatLayout.slots,
      columns: seatLayout.columns,
      index,
      studentId: student.id,
      shiftKey: event.shiftKey,
      toggleKey: event.ctrlKey || event.metaKey,
      currentSelection: selectedStudentIds,
      anchorIndex: selectionAnchorRef.current
    });
    setSelectedStudentIds(nextSelection);
    selectionAnchorRef.current = anchorIndex;
    setSeatMenu(null);
  };

  const handleOpenEmail = (targets: LiveStudentSeat[]) => {
    if (targets.length === 0) return;
    setEmailStudents(targets);
    setShowEmailModal(true);
  };

  const runForSelectedStudents = async (
    targets: LiveStudentSeat[],
    action: (studentId: string) => Promise<void>,
    filter?: (student: LiveStudentSeat) => boolean
  ) => {
    const list = filter ? targets.filter(filter) : targets;
    for (const student of list) {
      await action(student.id);
    }
  };

  const activeCompetenciesForPicker = useMemo(() => {
    const activeSet = new Set(session?.activeCompetencyIds || []);
    const picked = curriculumCompetencies.filter((entry) => activeSet.has(entry.id));
    return picked.map((entry) => ({ id: entry.id, name: entry.name }));
  }, [curriculumCompetencies, session?.activeCompetencyIds]);

  const handleSaveSession = async (nextSession: typeof session, nextObservations: StudentObservation[], nextHelpStates: TemporaryNeedHelpState[]) => {
    if (!nextSession) return;
    await saveAll(nextSession, nextObservations, nextHelpStates);
  };

  const handleSelectPhase = async (phaseId: string) => {
    if (!session) return;
    const next = { ...session, currentPhaseId: phaseId, teacherStateVersion: session.teacherStateVersion + 1 };
    setSession(next);
    await handleSaveSession(next, observations, helpStates);
    await emitEvent('phase_changed', { phaseId });
  };

  const handleConnectBoard = async () => {
    if (!session) return;
    if (!displayPinInput.trim()) {
      toast.error('Bitte PIN eingeben');
      return;
    }
    await connectQuickstartDisplay({
      pin: displayPinInput.trim(),
      teachingUnitId,
      className,
      theme
    });
    const next = { ...session, displayPin: displayPinInput.trim().toUpperCase() };
    setSession(next);
    await handleSaveSession(next, observations, helpStates);
    toast.success('Board verbunden');
  };

  const handleToggleHelp = async (studentId: string) => {
    if (!session) return;
    const now = new Date().toISOString();
    const has = helpStates.find((entry) => entry.studentId === studentId && entry.active);
    const nextHelp = has
      ? helpStates.map((entry) => (entry.studentId === studentId ? { ...entry, active: false } : entry))
      : [...helpStates.filter((entry) => entry.studentId !== studentId), { studentId, active: true, since: now }];
    setHelpStates(nextHelp);
    await handleSaveSession(session, observations, nextHelp);
    await emitEvent('help_toggled', { studentId, active: !has });
  };

  const handleAddObservation = async (studentId: string) => {
    if (!session) return;
    const nextObservation: StudentObservation = {
      id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}`,
      studentId,
      teachingUnitId,
      sessionId: session.id,
      preset: 'participation',
      note: '',
      status: 'draft',
      createdAt: new Date().toISOString()
    };
    const nextObservations = [...observations, nextObservation];
    setObservations(nextObservations);
    await handleSaveSession(session, nextObservations, helpStates);
    await emitEvent('observation_recorded', { studentId, observationId: nextObservation.id });
  };

  const handleToggleCompetency = async (competencyId: number) => {
    if (!session) return;
    const current = session.activeCompetencyIds || [];
    const nextIds = current.includes(competencyId)
      ? current.filter((id) => id !== competencyId)
      : [...current, competencyId];
    const next = { ...session, activeCompetencyIds: nextIds };
    setSession(next);
    await handleSaveSession(next, observations, helpStates);
  };

  const handleAddCompetency = async (studentId: string, competencyId: number) => {
    if (!session?.activeCompetencyIds?.includes(competencyId)) {
      toast.error('Kompetenz ist für diese Stunde nicht aktiv');
      return;
    }
    setSeatActionStudentId(studentId);
    try {
      await addQuickstartCompetencyBundle(teachingUnitId, {
        studentId,
        competencyIds: [competencyId],
        displayToastsEnabled: true,
        displayRankingEnabled: false
      });
      setBookingCounts((prev) => ({ ...prev, [studentId]: (prev[studentId] || 0) + 1 }));
      await emitEvent('competency_recorded', { studentId, competencyId });
      toast.success('Kompetenz vergeben');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Kompetenz konnte nicht vergeben werden');
    } finally {
      setSeatActionStudentId(null);
    }
  };

  const handleAwardActiveCompetencies = async (student: LiveStudentSeat) => {
    const activeIds = session?.activeCompetencyIds || [];
    if (!activeIds.length) {
      toast.error('Bitte zuerst Kompetenzen in der Liste aktivieren');
      return;
    }
    setSeatActionStudentId(student.id);
    try {
      await addQuickstartCompetencyBundle(teachingUnitId, {
        studentId: student.id,
        competencyIds: activeIds,
        displayToastsEnabled: true,
        displayRankingEnabled: false
      });
      setBookingCounts((prev) => ({ ...prev, [student.id]: (prev[student.id] || 0) + 1 }));
      await emitEvent('competency_recorded', { studentId: student.id, competencyIds: activeIds });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Beleg konnte nicht vergeben werden');
    } finally {
      setSeatActionStudentId(null);
    }
  };

  const handleRemoveLastBundle = async (studentId: string) => {
    setSeatActionStudentId(studentId);
    try {
      await removeQuickstartCompetencyBundle(teachingUnitId, {
        studentId,
        displayToastsEnabled: true,
        displayRankingEnabled: false
      });
      setBookingCounts((prev) => ({
        ...prev,
        [studentId]: Math.max(0, (prev[studentId] || 0) - 1)
      }));
      toast.success('Letzter Beleg entfernt');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Letzter Beleg nicht gefunden');
    } finally {
      setSeatActionStudentId(null);
    }
  };

  const handleToggleAbsence = async (studentId: string) => {
    const isAway = Boolean(activeAbsences[studentId]);
    setSeatActionStudentId(studentId);
    try {
      if (isAway) {
        await endTemporaryAbsence('teaching_unit', teachingUnitId, studentId);
        setActiveAbsences((prev) => {
          const next = { ...prev };
          delete next[studentId];
          return next;
        });
        toast.success('Schüler wieder anwesend');
      } else {
        const result = await startTemporaryAbsence('teaching_unit', teachingUnitId, studentId);
        setActiveAbsences((prev) => ({
          ...prev,
          [studentId]: {
            id: result.data.id,
            reason: result.data.reason,
            started_at: result.data.started_at
          }
        }));
        toast.success('Als abgängig gemeldet');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Abwesenheit konnte nicht geändert werden');
    } finally {
      setSeatActionStudentId(null);
    }
  };

  const awayStudentIds = useMemo(() => new Set(Object.keys(activeAbsences)), [activeAbsences]);

  const buildSeatMenuItems = (targets: LiveStudentSeat[]): StudentSeatMenuItem[] => {
      const isGroup = targets.length > 1;
      const allAway = targets.length > 0 && targets.every((student) => Boolean(activeAbsences[student.id]));
      const anyHasBundle = targets.some((student) => (student.bookingCount || 0) >= 1);

      return [
        {
          id: 'help',
          label: 'Hilfebedarf',
          onClick: () => void runForSelectedStudents(targets, handleToggleHelp)
        },
        {
          id: 'single',
          label: 'Einzelkompetenz',
          disabled: isGroup,
          onClick: () => {
            setSelectedStudent(targets[0]);
            setShowCompetencyPicker(true);
          }
        },
        {
          id: 'note',
          label: 'Notiz',
          onClick: () => {
            void (async () => {
              await runForSelectedStudents(targets, handleAddObservation);
              toast.success(isGroup ? `Notiz für ${targets.length} Schüler erfasst` : 'Notiz erfasst');
            })();
          }
        },
        {
          id: 'absence',
          label: allAway ? 'Wieder anwesend' : 'Abgängig',
          onClick: () => void runForSelectedStudents(targets, handleToggleAbsence)
        },
        {
          id: 'email',
          label: 'E-Mail',
          onClick: () => handleOpenEmail(targets)
        },
        {
          id: 'undo',
          label: 'Letzten Beleg löschen',
          destructive: true,
          disabled: !anyHasBundle,
          onClick: () =>
            void runForSelectedStudents(
              targets,
              handleRemoveLastBundle,
              (student) => (student.bookingCount || 0) >= 1
            )
        }
      ];
  };

  const groupMenuItems = useMemo(
    () => buildSeatMenuItems(selectedStudents),
    [selectedStudents, activeAbsences, bookingCounts]
  );

  const seatMenuStudentName = seatMenu ? `${seatMenu.student.firstName} ${seatMenu.student.lastName}` : '';
  const currentDraftCount = observations.filter((entry) => entry.status === 'draft').length;

  const handleDisconnectBoard = async () => {
    if (!session) return;
    await disconnectQuickstartDisplay(session.displayPin || '');
    const next = { ...session, displayPin: null };
    setSession(next);
    await handleSaveSession(next, observations, helpStates);
  };

  if (loading || !session) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_320px]">
        <div className="space-y-4 min-w-0">
          <Tile>
            <TileHeader className="flex flex-row items-center justify-between gap-2">
              <TileTitle>Sitzordnung · Live</TileTitle>
              <Button size="sm" variant="ghost" onClick={onExitToOverview}>
                Übersicht
              </Button>
            </TileHeader>
            <TileContent>
              {contextLoading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Sitzplan wird geladen…
                </div>
              ) : (
                <>
                  {sitzplanSuggestions.length > 0 ? (
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Sitzplan:</span>
                      <select
                        value={selectedSitzplanId ? String(selectedSitzplanId) : ''}
                        onChange={(event) => setSelectedSitzplanId(event.target.value ? Number(event.target.value) : null)}
                        className="input-field w-72"
                      >
                        {sitzplanSuggestions.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name}
                          </option>
                        ))}
                      </select>
                      <StudentSeatGroupActionsMenu selectedCount={selectedStudents.length} items={groupMenuItems} />
                      {selectedStudents.length > 0 ? (
                        <button
                          type="button"
                          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          onClick={() => {
                            setSelectedStudentIds(new Set());
                            selectionAnchorRef.current = null;
                          }}
                        >
                          Auswahl aufheben
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <StudentSeatGroupActionsMenu selectedCount={selectedStudents.length} items={groupMenuItems} />
                      {selectedStudents.length > 0 ? (
                        <button
                          type="button"
                          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                          onClick={() => {
                            setSelectedStudentIds(new Set());
                            selectionAnchorRef.current = null;
                          }}
                        >
                          Auswahl aufheben
                        </button>
                      ) : null}
                    </div>
                  )}
                  <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                    Strg/Cmd für Einzelauswahl, Shift für Rechteckauswahl. Kontextmenü per Rechtsklick oder langem Drücken.
                  </p>
                  <SeatingLiveCanvas
                    students={seatLayout.slots}
                    columns={seatLayout.columns}
                    helpStates={helpStates}
                    awayStudentIds={awayStudentIds}
                    busyStudentId={seatActionStudentId}
                    selectedStudentIds={selectedStudentIds}
                    onStudentAward={(student) => void handleAwardActiveCompetencies(student)}
                    onStudentSelect={handleStudentSelect}
                    onStudentOpenMenu={(student, coords) =>
                      setSeatMenu({ student, x: coords.clientX, y: coords.clientY })
                    }
                  />
                </>
              )}
            </TileContent>
          </Tile>
        </div>

        <div className="space-y-4">
          <LessonPhaseTimeline
            phases={PHASES}
            activePhaseId={session.currentPhaseId}
            onSelect={handleSelectPhase}
          />
          <LessonCompetencyPanel
            competencies={curriculumCompetencies}
            activeIds={session.activeCompetencyIds || []}
            suggestedIds={session.suggestedCompetencyIds || []}
            loading={competencyLoading}
            suggesting={competencySuggesting}
            suggestSource={competencySuggestSource ?? session.competencySuggestSource ?? null}
            onToggle={(competencyId) => void handleToggleCompetency(competencyId)}
          />
          <TeacherControlDock
            workMode={session.workMode}
            onWorkModeChange={async (mode) => {
              const next = { ...session, workMode: mode, teacherStateVersion: session.teacherStateVersion + 1 };
              setSession(next);
              await handleSaveSession(next, observations, helpStates);
              await emitEvent('work_mode_changed', { mode });
            }}
            scene={session.activeScene}
            onSceneChange={async (scene) => {
              const next = { ...session, activeScene: scene, boardStateVersion: session.boardStateVersion + 1 };
              setSession(next);
              await handleSaveSession(next, observations, helpStates);
              await emitEvent('board_scene_changed', { scene });
            }}
            timerSec={timerSec}
            timerRunning={timerRunning}
            onToggleTimer={async () => {
              setTimerRunning((prev) => !prev);
              await emitEvent(timerRunning ? 'timer_paused' : 'timer_started', { remainingSec: timerSec });
            }}
            onResetTimer={async (seconds) => {
              setTimerSec(seconds);
              await emitEvent('timer_reset', { remainingSec: seconds });
            }}
            displayPin={session.displayPin ?? null}
            displayPinInput={displayPinInput}
            onDisplayPinInputChange={setDisplayPinInput}
            onConnectBoard={() => void handleConnectBoard()}
            onDisconnectBoard={() => void handleDisconnectBoard()}
          />
          <RoomEnergyWidget
            signal={acoustic.signal}
            history={acoustic.history}
            audioEnabled={audioEnabled}
            isLoadingModels={acoustic.isLoadingModels}
            onToggleAudio={() => setAudioEnabled((prev) => !prev)}
          />
          <LessonSessionManagement
            draftObservationCount={currentDraftCount}
            ending={endingSession}
            destroying={destroyingSession}
            onEndSession={async () => {
              setEndingSession(true);
              try {
                const next = { ...session, status: 'ended' as const, endedAt: new Date().toISOString() };
                setSession(next);
                await handleSaveSession(next, observations, helpStates);
                await emitEvent('session_ended', {});
                setReviewOpen(true);
              } finally {
                setEndingSession(false);
              }
            }}
            onDestroySession={async () => {
              setDestroyingSession(true);
              try {
                if (session.displayPin) {
                  try {
                    await disconnectQuickstartDisplay(session.displayPin);
                  } catch {
                    // Backend trennt die Anzeige ebenfalls.
                  }
                }
                await destroyLiveSession(teachingUnitId);
                toast.success('Session vernichtet');
                onExitToOverview();
              } catch (error: unknown) {
                const err = error as { response?: { data?: { error?: string } } };
                toast.error(err.response?.data?.error || 'Session konnte nicht vernichtet werden');
                throw error;
              } finally {
                setDestroyingSession(false);
              }
            }}
          />
        </div>
      </div>

      <StudentSeatContextMenu
        open={Boolean(seatMenu)}
        x={seatMenu?.x ?? 0}
        y={seatMenu?.y ?? 0}
        studentName={seatMenuStudentName}
        onClose={() => setSeatMenu(null)}
        items={seatMenu ? buildSeatMenuItems([seatMenu.student]) : []}
      />

      <CompetencyQuickPicker
        isOpen={showCompetencyPicker}
        onClose={() => {
          setShowCompetencyPicker(false);
          setSelectedStudent(null);
        }}
        competencies={activeCompetenciesForPicker}
        onPick={async (competencyId) => {
          if (!selectedStudent) return;
          await handleAddCompetency(selectedStudent.id, competencyId);
          setShowCompetencyPicker(false);
          setSelectedStudent(null);
        }}
      />

      <LiveSessionReview
        isOpen={reviewOpen}
        observations={observations}
        onClose={() => setReviewOpen(false)}
        onCommit={async (ids) => {
          await commitReview(ids);
          toast.success('Beobachtungen übernommen');
          setReviewOpen(false);
          onExitToOverview();
        }}
        onDiscard={async (ids) => {
          await discardReview(ids);
          toast.success('Beobachtungen verworfen');
          setReviewOpen(false);
          onExitToOverview();
        }}
      />

      {emailStudents.length > 0 && (
        <EmailModal
          isOpen={showEmailModal}
          onClose={() => {
            setShowEmailModal(false);
            setEmailStudents([]);
          }}
          to={undefined}
          studentName={
            emailStudents.length === 1
              ? `${emailStudents[0].firstName} ${emailStudents[0].lastName} (${className})`
              : `${emailStudents.length} Schüler (${className})`
          }
          studentUnid={emailStudents.length === 1 ? emailStudents[0].id : undefined}
          studentUnids={emailStudents.length > 1 ? emailStudents.map((student) => student.id) : undefined}
          context="sitzordnung"
          initialSubject={
            emailStudents.length === 1
              ? `Nachricht zu ${emailStudents[0].firstName} ${emailStudents[0].lastName}`
              : 'Nachricht zu ausgewählten Schülerinnen und Schülern'
          }
          initialBody={
            emailStudents.length === 1
              ? `Sehr geehrte Eltern,\n\nbezüglich Ihres Kindes ${emailStudents[0].firstName} ${emailStudents[0].lastName} (${className}).\n\nMit freundlichen Grüßen`
              : `Sehr geehrte Eltern,\n\nbezüglich Ihrer Kinder (${className}).\n\nMit freundlichen Grüßen`
          }
        />
      )}
    </div>
  );
}

export default function LiveLessonPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [metaClasses, setMetaClasses] = useState<Array<{ id: string | number | null; class_name: string }>>([]);
  const [metaSubjects, setMetaSubjects] = useState<Array<{ id: number; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [teachingUnitId, setTeachingUnitId] = useState<number | null>(null);
  const [className, setClassName] = useState('');
  const [classId, setClassId] = useState<string>('');
  const [theme, setTheme] = useState('Lernziel');
  const [subjectId, setSubjectId] = useState<number | undefined>(undefined);
  const [timetableHint, setTimetableHint] = useState<string | null>(null);
  const [savedSessions, setSavedSessions] = useState<LiveSessionListItem[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [resumingId, setResumingId] = useState<number | null>(null);

  const loadSavedSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const sessions = await fetchLiveSessions();
      setSavedSessions(sessions);
    } catch {
      toast.error('Gespeicherte Sessions konnten nicht geladen werden');
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const [meta, timetable] = await Promise.all([fetchTeachingMeta(), fetchTimetableNowWidget()]);
        if (!mounted) return;
        setMetaClasses((meta.classes || []).map((entry) => ({ id: entry.id, class_name: entry.class_name })));
        setMetaSubjects((meta.subjects || []).map((entry) => ({ id: entry.id, name: entry.name || 'Fach' })));
        const firstNow = (timetable.entries || [])[0];
        if (firstNow?.class_name) {
          setClassName(firstNow.class_name);
          const matched = (meta.classes || []).find((entry) => entry.class_name === firstNow.class_name);
          if (matched?.id != null) setClassId(String(matched.id));
        }
        if (firstNow?.subject_name) setTheme(firstNow.subject_name);
        if (firstNow?.class_name || firstNow?.subject_name) {
          setTimetableHint(`Stundenplan erkannt: ${firstNow.class_name || ''} ${firstNow.subject_name || ''}`.trim());
        }
      } catch {
        toast.error('Live-Metadaten konnten nicht geladen werden');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    void loadSavedSessions();
    return () => {
      mounted = false;
    };
  }, [loadSavedSessions]);

  useEffect(() => {
    const unitParam = searchParams.get('unitId');
    if (!unitParam || teachingUnitId) return;
    const parsed = Number(unitParam);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    setTeachingUnitId(parsed);
  }, [searchParams, teachingUnitId]);

  useEffect(() => {
    if (!teachingUnitId) return;
    let mounted = true;
    void fetchLiveLessonSession(teachingUnitId)
      .then(({ session }) => {
        if (!mounted || !session) return;
        if (session.classContext?.label) {
          setClassName(session.classContext.label);
          const matched = metaClasses.find((c) => c.class_name === session.classContext.label);
          setClassId(matched?.id != null ? String(matched.id) : '');
        }
        if (session.theme) setTheme(session.theme);
        if (session.subjectId != null) setSubjectId(session.subjectId);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [teachingUnitId, metaClasses]);

  const handleResume = async (entry: LiveSessionListItem) => {
    setResumingId(entry.teachingUnitId);
    try {
      if (entry.liveStatus === 'ended') {
        await reactivateLiveSession(entry.teachingUnitId);
      }
      if (entry.classLabel) {
        setClassName(entry.classLabel);
        const matched = metaClasses.find((c) => c.class_name === entry.classLabel);
        setClassId(matched?.id != null ? String(matched.id) : '');
      }
      if (entry.theme) setTheme(entry.theme);
      setSubjectId(entry.subjectId ?? undefined);
      setTeachingUnitId(entry.teachingUnitId);
      setSearchParams({ unitId: String(entry.teachingUnitId) });
      toast.success(entry.liveStatus === 'ended' ? 'Session reaktiviert' : 'Session fortgesetzt');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Session konnte nicht geladen werden');
    } finally {
      setResumingId(null);
    }
  };

  const handleStart = async () => {
    if (!className.trim()) {
      toast.error('Bitte Klasse wählen');
      return;
    }
    setStarting(true);
    try {
      const parsedParentId = Number(classId);
      const safeParentId = Number.isFinite(parsedParentId) && parsedParentId > 0 ? parsedParentId : undefined;
      const unit = await createTeachingUnitApi({
        title: `Live-Unterricht ${className} ${new Date().toLocaleDateString('de-DE')}`,
        parent_type: 'class',
        parent_id: safeParentId,
        status: 'active'
      });
      const unitId = Number(unit?.id);
      if (!unitId) {
        throw new Error('teaching unit id missing');
      }
      await updateTeachingUnitApi(unitId, { status: 'active' });
      await saveLiveLessonSession(unitId, {
        session: buildInitialLiveSession({
          teachingUnitId: unitId,
          className,
          theme,
          subjectId
        }),
        observations: [],
        helpStates: []
      });
      setTeachingUnitId(unitId);
      setSearchParams({ unitId: String(unitId) });
      await loadSavedSessions();
      toast.success('Live-Unterricht gestartet');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Live-Unterricht konnte nicht gestartet werden');
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!teachingUnitId) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Tile>
          <TileHeader>
            <TileTitle>Live-Unterricht starten</TileTitle>
          </TileHeader>
          <TileContent className="space-y-3">
            {timetableHint ? <div className="rounded-md bg-primary-50 px-3 py-2 text-sm text-primary-700">{timetableHint}</div> : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Klasse</label>
                <select
                  value={className}
                  onChange={(event) => {
                    const nextClassName = event.target.value;
                    setClassName(nextClassName);
                    const matched = metaClasses.find((entry) => entry.class_name === nextClassName);
                    setClassId(matched?.id != null ? String(matched.id) : '');
                  }}
                  className="input-field"
                >
                  <option value="">Bitte wählen</option>
                  {metaClasses.map((entry) => (
                    <option key={`${entry.id ?? entry.class_name}`} value={entry.class_name}>
                      {entry.class_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Fach (optional)</label>
                <select
                  value={subjectId ? String(subjectId) : ''}
                  onChange={(event) => setSubjectId(event.target.value ? Number(event.target.value) : undefined)}
                  className="input-field"
                >
                  <option value="">—</option>
                  {metaSubjects.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Thema / Aufgabe</label>
              <input value={theme} onChange={(event) => setTheme(event.target.value)} className="input-field" />
            </div>
            <Button variant="primary" size="lg" className="w-full" onClick={handleStart} disabled={starting}>
              {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Stunde starten
            </Button>
          </TileContent>
        </Tile>
        <LiveSessionHistoryList
          sessions={savedSessions}
          loading={sessionsLoading}
          resumingId={resumingId}
          onResume={handleResume}
        />
      </div>
    );
  }

  const handleExitToOverview = () => {
    setTeachingUnitId(null);
    setSearchParams({});
    void loadSavedSessions();
  };

  return (
    <LiveSessionProvider teachingUnitId={teachingUnitId}>
      <LiveLessonShell
        teachingUnitId={teachingUnitId}
        className={className}
        theme={theme}
        subjectId={subjectId}
        onExitToOverview={handleExitToOverview}
      />
    </LiveSessionProvider>
  );
}
